import {doc, updateDoc} from 'firebase/firestore';
import {db} from '../firebase';

// Persists the provider's own phone number on their `User` document so the
// phone-encounter form can prefill it next time. The value is stored under the
// snake_case `provider_phone` key to match the rest of the User doc shape that
// UserContext reads.
export async function saveProviderPhone(
  userId: string,
  providerPhone: string,
): Promise<void> {
  const trimmed = String(providerPhone || '').trim();
  if (!userId || !trimmed) return;
  await updateDoc(doc(db, 'User', userId), {provider_phone: trimmed});
}

export async function saveInterpreterAutoUnmuteOnConnect(
  userId: string,
  enabled: boolean,
): Promise<void> {
  if (!userId) return;
  await updateDoc(doc(db, 'User', userId), {
    interpreter_auto_unmute_on_connect: Boolean(enabled),
  });
}

// Splits a legacy single-field name into first/last on the first whitespace, so
// older accounts that only stored `provider_name` can be backfilled into the
// structured `first_name` / `last_name` fields. Everything after the first
// space is treated as the last name ("Jane Doe Smith" -> Jane / Doe Smith).
export function splitProviderName(name: string | null | undefined): {
  first: string;
  last: string;
} {
  const trimmed = String(name || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!trimmed) return {first: '', last: ''};
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) return {first: trimmed, last: ''};
  return {
    first: trimmed.slice(0, spaceIndex),
    last: trimmed.slice(spaceIndex + 1),
  };
}

export interface ProviderProfileInput {
  firstName: string;
  lastName: string;
  title?: string;
  providerPhone?: string;
  spokenLanguages?: string[];
}

// Persists the structured profile collected during onboarding. Writes the new
// snake_case fields and keeps the legacy `provider_name` in sync so existing
// readers (e.g. the planner greeting) keep working, then flags onboarding as
// complete so the blocking modal doesn't reappear.
export async function saveProviderProfile(
  userId: string,
  profile: ProviderProfileInput,
): Promise<void> {
  if (!userId) return;
  const firstName = String(profile.firstName || '').trim();
  const lastName = String(profile.lastName || '').trim();
  const title = String(profile.title || '').trim();
  const providerPhone = String(profile.providerPhone || '').trim();
  const spokenLanguages = Array.isArray(profile.spokenLanguages)
    ? profile.spokenLanguages
        .map(language => String(language).trim())
        .filter(Boolean)
    : [];
  const providerName = [firstName, lastName].filter(Boolean).join(' ');

  const payload: Record<string, unknown> = {
    first_name: firstName,
    last_name: lastName,
    provider_title: title,
    spoken_languages: spokenLanguages,
    provider_name: providerName,
    onboarding_completed: true,
    onboarding_completed_at: new Date().toISOString(),
  };
  if (providerPhone) payload.provider_phone = providerPhone;

  await updateDoc(doc(db, 'User', userId), payload);
}

export default {
  saveProviderPhone,
  saveInterpreterAutoUnmuteOnConnect,
  splitProviderName,
  saveProviderProfile,
};
